import { Box, Flex, Text, HStack, IconButton, Button, Spinner } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CopyIcon } from '@assets/icons';
import { useChatStore } from '@store/chat/chat.store.jsx';
import { useBreakpoints, useCopyToClipboard, useStyledToast } from '@util/hooks';
import { MessageContent } from './chat-message-content.component';
import { useState } from 'react';
import { useUserStore } from '@store/app.store';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const ArtifactContent = () => {
  const { isMobile } = useBreakpoints();
  const conversation = useChatStore((state) => state.conversation);
  const activeArtifactIndex = useChatStore((state) => state.activeArtifactIndex);
  return (
    <Box
      w='100%'
      pb={isMobile ? 100 : 3}
      maxW='720px'
    >
      <Flex
        flexDirection='column'
        h={isMobile ? '100%' : '95%'}
        overflowY='auto'
        p={5}
        bg='white'
        borderRadius={8}
        px={10}
      >
        <Box
          flex='1'
          overflowY='auto'
          maxHeight='65vh'
          style={{ scrollbarWidth: 'none' }}
        >
          <MessageContent
            content={
              conversation?.artifacts?.length
                ? conversation?.artifacts[activeArtifactIndex]?.content
                : ''
            }
          />
        </Box>
        <ArtifactActionButtons />
      </Flex>
    </Box>
  );
};

const ArtifactActionButtons = () => {
  const user = useUserStore((state) => state.user);
  const copyToClipboard = useCopyToClipboard();
  const conversation = useChatStore((state) => state.conversation);
  const activeArtifactIndex = useChatStore((state) => state.activeArtifactIndex);
  const setActiveArtifactIndex = useChatStore((state) => state.setActiveArtifactIndex);
  const saveAsTemplate = useChatStore((state) => state.saveAsTemplate);
  const toast = useStyledToast();
  const queryClient = useQueryClient();
  const closeFn = useChatStore((state) => state.closeFn);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [, setSearchParams] = useSearchParams();

  if (!conversation?.artifacts?.length) return null;

  const saveTemplate = async () => {
    if (!saveAsTemplate) return;
    setIsSavingTemplate(true);
    try {
      const { initiative } = await saveAsTemplate(user);
      await queryClient.invalidateQueries({ queryKey: ['cards'] });
      setSearchParams((searchParams) => {
        searchParams.set('initiativeId', initiative.id);
        return searchParams;
      });
      if (closeFn) {
        closeFn();
      }
      toast({
        title: 'Template Saved!',
        status: 'success',
        isClosable: true
      });
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setContext('chat', { conversationId: conversation?.id });
        scope.setExtra('error_message', 'Error saving template');
        Sentry.captureException(e);
      });
      toast({
        title: 'Template Save Error',
        description: `${e}`,
        status: 'error',
        isClosable: true
      });
      setIsSavingTemplate(false);
    }
    setIsSavingTemplate(false);
  };

  return (
    <Box>
      <HStack
        justify='space-between'
        pt={4}
      >
        <HStack>
          <IconButton
            icon={<ChevronLeftIcon />}
            variant='ghost'
            aria-label='Previous Artifact Version'
            size='sm'
            bgColor='surface.containerLow'
            onClick={() => {
              if (activeArtifactIndex > 0) {
                setActiveArtifactIndex(activeArtifactIndex - 1);
              }
            }}
          />
          <Text
            color='surface.onSurfaceVariant'
            fontSize='sm'
          >
            {`Version ${activeArtifactIndex + 1} of ${conversation.artifacts.length}`}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            variant='ghost'
            aria-label='Next Artifact Version'
            size='sm'
            bgColor='surface.containerLow'
            onClick={() => {
              if (activeArtifactIndex < conversation?.artifacts?.length - 1) {
                setActiveArtifactIndex(activeArtifactIndex + 1);
              }
            }}
          />
        </HStack>

        <HStack spacing={2}>
          <IconButton
            icon={<CopyIcon />}
            variant='ghost'
            aria-label='Copy'
            size='sm'
            bgColor='surface.containerLow'
            onClick={() => {
              copyToClipboard(conversation.artifacts[activeArtifactIndex].content);
            }}
          />
          {saveAsTemplate && (
            <Button
              variant='reskinPrimary'
              size='md'
              onClick={() => saveTemplate()}
              disabled={isSavingTemplate || conversation.messages.length < 2}
            >
              {isSavingTemplate && (
                <Spinner
                  size='xs'
                  mr='10px'
                />
              )}
              Save as Template
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );
};

export default ArtifactContent;
